import { Capacitor } from '@capacitor/core';
import { useSettings } from '../store/settings';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform() as 'web' | 'ios' | 'android';

const env = import.meta.env;
const sameOrigin = typeof window !== 'undefined' ? window.location.origin : '';
export const API_URL: string = env.VITE_API_URL || sameOrigin;
export const WS_URL: string = env.VITE_WS_URL || API_URL.replace(/^http/, 'ws');

export const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
/**
 * Run `fn` once the page has settled: after the load event and an idle moment (at most `maxWaitMs`
 * after load), or at the visitor's first tap or key press, whichever comes first. For scripts that
 * shouldn't compete with the first screen for bandwidth and CPU.
 */
export function afterStartup(fn: () => void, maxWaitMs = 3000): void {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    for (const e of ['pointerdown', 'keydown'] as const) window.removeEventListener(e, run, true);
    fn();
  };
  for (const e of ['pointerdown', 'keydown'] as const) window.addEventListener(e, run, { capture: true, passive: true });
  const idle = () => (typeof requestIdleCallback === 'function' ? requestIdleCallback(run, { timeout: maxWaitMs }) : window.setTimeout(run, maxWaitMs));
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}

export const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

type HapticKind = 'light' | 'medium' | 'heavy' | 'success' | 'error';
/** Soft vibration: every impact one step lighter. */
const SOFTER: Record<HapticKind, HapticKind> = { light: 'light', medium: 'light', heavy: 'medium', success: 'light', error: 'medium' };

/** Vibrate per the player's setting (off / soft / strong). */
export async function haptic(kind: HapticKind = 'light'): Promise<void> {
  const level = useSettings.getState().haptics;
  if (level === 'off') return;
  if (level === 'soft') kind = SOFTER[kind];
  if (isNative) {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
    if (kind === 'success' || kind === 'error') {
      await Haptics.notification({ type: kind === 'success' ? NotificationType.Success : NotificationType.Error });
    } else {
      await Haptics.impact({ style: kind === 'light' ? ImpactStyle.Light : kind === 'medium' ? ImpactStyle.Medium : ImpactStyle.Heavy });
    }
  } else if ('vibrate' in navigator) {
    navigator.vibrate(kind === 'heavy' || kind === 'error' ? 40 : kind === 'medium' ? 20 : 12);
  }
}

/**
 * Publishes the on-screen keyboard's height as `--kb` on <html>, so fixed popups can sit above it
 * (iOS and Android browsers overlay the keyboard on fixed elements instead of resizing them).
 */
export function trackKeyboardInset(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  const update = () => {
    const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    // Small differences are browser chrome (URL bar) settling, not a keyboard.
    const open = kb > 80;
    document.documentElement.style.setProperty('--kb', `${open ? Math.round(kb) : 0}px`);
    document.documentElement.classList.toggle('keyboard-open', open);
  };
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  update();
}

/** Native setup: status bar, splash, Android back button, deep links (invite codes). */
export async function initNative(onDeepLink: (path: string) => void): Promise<void> {
  if (!isNative) return;
  // The web page allows pinch-zoom for accessibility; the native apps keep a fixed, app-like scale.
  document.querySelector('meta[name="viewport"]')?.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no');
  const [{ App }, { SplashScreen }, { StatusBar, Style }] = await Promise.all([
    import('@capacitor/app'),
    import('@capacitor/splash-screen'),
    import('@capacitor/status-bar'),
  ]);
  await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
  await SplashScreen.hide().catch(() => {});
  await App.addListener('appUrlOpen', ({ url }) => {
    try {
      const u = new URL(url);
      onDeepLink(u.pathname + u.search);
    } catch {
      /* ignore malformed links */
    }
  });
  await App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) window.history.back();
    else void App.minimizeApp();
  });
}
